import { useEffect, useState, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  MessageSquareQuote,
  BriefcaseBusiness,
  Rocket,
  Building2,
  Handshake,
  Bolt,
  BookOpen,
} from "lucide-react"

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>
  label: string
  id: string
}

const MENU_ITEMS: MenuItem[] = [
  { icon: Rocket,             label: "Let's Start",  id: "start"        },
  { icon: Building2,          label: "About Us",     id: "about"        },
  { icon: Bolt,               label: "Capabilities", id: "capabilities" },
  { icon: BriefcaseBusiness,  label: "Projects",     id: "projects"     },
  { icon: MessageSquareQuote, label: "Testimonials", id: "digital"      },
  { icon: BookOpen,           label: "Blog",         id: "career"       },
  { icon: Handshake,          label: "Partners",     id: "partners"     },
]

const ROW_HEIGHT = 78
const ICON_HEIGHT = 50
const PILL_HEIGHT = 40
const PILL_OFFSET = (ICON_HEIGHT - PILL_HEIGHT) / 2

export default function Sidebar() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [visible, setVisible] = useState(true)
  const isOnFooterRef = useRef(false)
  const prevIndexRef = useRef(0)
  const isScrollingRef = useRef(false)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const pillY = activeIndex * ROW_HEIGHT + PILL_OFFSET

  const movePill = useCallback((nextIndex: number) => {
    prevIndexRef.current = nextIndex
    setActiveIndex(nextIndex)
  }, [])

  // Single observer — picks whichever section has the largest intersection ratio
  useEffect(() => {
    const ratioMap = new Map<string, number>()

    const observer = new IntersectionObserver(
      (entries) => {
        // Skip observer updates while user just clicked (let click movePill win)
        if (isScrollingRef.current) return

        entries.forEach((entry) => {
          ratioMap.set(entry.target.id, entry.intersectionRatio)
        })

        let bestId = ""
        let bestRatio = 0
        ratioMap.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio
            bestId = id
          }
        })

        if (bestId && bestRatio > 0.15) {
          const index = MENU_ITEMS.findIndex((m) => m.id === bestId)
          if (index !== -1 && index !== prevIndexRef.current) {
            movePill(index)
          }
        }
      },
      {
        threshold: [0, 0.1, 0.15, 0.2, 0.3, 0.5, 0.75, 1.0],
      }
    )

    MENU_ITEMS.forEach((item) => {
      const el = document.getElementById(item.id)
      if (el) {
        ratioMap.set(item.id, 0)
        observer.observe(el)
      }
    })

    return () => observer.disconnect()
  }, [movePill])

  // Footer visibility
  useEffect(() => {
    const onFooterEnter = () => {
      isOnFooterRef.current = true
      setVisible(false)
    }
    const onFooterLeave = () => {
      isOnFooterRef.current = false
      if (!document.body.classList.contains("hide-sidebar")) {
        setVisible(true)
      }
    }
    window.addEventListener("footer:enter", onFooterEnter)
    window.addEventListener("footer:leave", onFooterLeave)
    return () => {
      window.removeEventListener("footer:enter", onFooterEnter)
      window.removeEventListener("footer:leave", onFooterLeave)
    }
  }, [])

  // Body class observer
  useEffect(() => {
    const classObserver = new MutationObserver(() => {
      const hiddenByClass = document.body.classList.contains("hide-sidebar")
      if (hiddenByClass) {
        setVisible(false)
      } else if (!isOnFooterRef.current) {
        setVisible(true)
      }
    })
    classObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] })
    return () => classObserver.disconnect()
  }, [])

  // Click: lock observer for 1s so pill doesn't glitch during smooth scroll
  const handleClick = useCallback((item: MenuItem, index: number) => {
    const el = document.getElementById(item.id)
    if (!el) return

    isScrollingRef.current = true
    movePill(index)
    el.scrollIntoView({ behavior: "smooth", block: "start" })

    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    scrollTimerRef.current = setTimeout(() => {
      isScrollingRef.current = false
    }, 1000)
  }, [movePill])

  return (
    <div
      className="fixed left-6 z-[200] hidden md:block"
      style={{ top: "55%", transform: "translateY(-50%)" }}
    >
      <motion.aside
        initial={{ x: -80, opacity: 0 }}
        animate={{ x: visible ? 0 : -80, opacity: visible ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        aria-hidden={!visible}
        className={[
          "flex flex-col",
          "bg-gradient-to-b from-white/15 to-white/5 backdrop-blur-xl",
          "px-2 pt-4 pb-6 rounded-2xl text-white w-[74px]",
          "border border-white/10 shadow-2xl",
        ].join(" ")}
      >
        <div className="relative flex flex-col gap-7 items-center">
          {/* Pill — driven by direct animate, no spring, no motion value */}
          <motion.div
            className="absolute -left-3 w-[3px] h-10 rounded-full pointer-events-none"
            animate={{ y: pillY }}
            transition={{ type: "tween", duration: 0.25, ease: "easeInOut" }}
            style={{ background: "linear-gradient(to bottom, #c084fc, #60a5fa)" }}
          />

          {MENU_ITEMS.map((item, index) => {
            const isActive = activeIndex === index
            const isHovered = hoveredItem === item.id
            const Icon = item.icon

            return (
              <div
                key={item.id}
                className="relative flex items-center justify-center flex-shrink-0"
                style={{ width: 50, height: 50 }}
              >
                <motion.button
                  onClick={() => handleClick(item, index)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.93 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  aria-label={item.label}
                  className="w-[50px] h-[50px] flex items-center justify-center rounded-xl cursor-pointer relative overflow-hidden"
                  style={{
                    backgroundColor: isActive ? "rgba(255,255,255,0.10)" : "transparent",
                    transition: "background-color 150ms ease-out",
                    pointerEvents: visible ? "auto" : "none",
                  }}
                >
                  <span
                    className="relative z-10 inline-flex"
                    style={{
                      filter: isActive ? "drop-shadow(0 0 6px rgba(168,85,247,0.7))" : "none",
                      transition: "filter 150ms ease-out",
                    }}
                  >
                    <Icon className={["w-6 h-6", isActive ? "text-white" : "text-white/50"].join(" ")} />
                  </span>

                  {!isActive && (
                    <div
                      className="absolute inset-0 rounded-xl"
                      style={{
                        background: isHovered ? "rgba(255,255,255,0.08)" : "transparent",
                        transition: "background 150ms ease-out",
                      }}
                    />
                  )}

                  <AnimatePresence>
                    {isHovered && !isActive && (
                      <motion.span
                        className="absolute inset-0 rounded-xl"
                        style={{ background: "rgba(255,255,255,0.12)" }}
                        initial={{ scale: 0, opacity: 1 }}
                        animate={{ scale: 1.7, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </motion.button>

                <div
                  className="absolute top-full left-1/2 pointer-events-none whitespace-nowrap"
                  style={{
                    marginTop: "4px",
                    transform: `translateX(-50%) translateY(${isActive ? "0px" : "-3px"})`,
                    opacity: isActive ? 1 : 0,
                    visibility: isActive ? "visible" : "hidden",
                    transition: "opacity 200ms ease-out, transform 200ms ease-out",
                  }}
                >
                  <p
                    className="text-[10px] font-semibold tracking-wide leading-tight mt-1"
                    style={{
                      background: "linear-gradient(to right, #d8b4fe, #93c5fd)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {item.label}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <motion.div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: "linear-gradient(to right, transparent, rgba(168,85,247,0.7), transparent)" }}
          animate={{ opacity: [0.2, 0.8, 0.2] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.aside>
    </div>
  )
}